import React from 'react';
import styled from 'styled-components';
import { transparentize } from 'polished';

import { useTheme } from '@material-ui/core/styles';

import {
  Box,
  Radio,
  Text,
  Columns,
  Column,
  OriginThemeProviderProps,
  useTracking,
} from '@origin-digital/ods-core';
import { Currency, formatContractAccount } from '@origin-digital/formatters';
import { IContractAccountInvoice } from '@origin-digital/payment-service-dapi-contract';

import { IContractAccountBalanceWithSacInfo, EPaymentType } from 'src/interfaces';
import { divisionIdLabelMap, divisionCodeMap } from 'src/consts';

import * as content from './content.json';

interface IProps {
  disabled?: boolean;
  contractAccountBalance: IContractAccountBalanceWithSacInfo;
  isSelected?: boolean;
  onClick: (contractAccountId: string, amount: number) => void;
  theme?: OriginThemeProviderProps;
}

interface IAmountInfo {
  amount: number;
  description: string;
  isCredit?: boolean;
}

const AccountSelectorTile = styled.button<Pick<IProps, 'isSelected'>>`
  width: 100%;
  display: block;
  border: 0;
  padding: 0;
  position: relative;
  outline: none;
  background: none;
  overflow: visible;
  text-align: left;

  > * {
    border: solid 1px
      ${({ isSelected, theme }) =>
        isSelected ? theme.palette.warning.main : theme.palette.grey[200]};
    padding: 16px;
  }

  &:not(:disabled) {
    cursor: pointer;

    &:hover > * {
      box-shadow: ${({ theme }) => `0 0 0 4px ${theme.palette.grey[100]}`};
      border-color: ${({ isSelected, theme }) =>
        isSelected ? theme.palette.warning.main : theme.palette.grey[300]};
    }

    &:focus > * {
      box-shadow: ${({ theme }) => `0 0 0 4px ${transparentize(0.68, theme.palette.warning.main)}`};
    }

    &:active > * {
      box-shadow: ${({ theme }) => `0 0 0 4px ${transparentize(0.84, theme.palette.warning.main)}`};
      transform: translate(0, 0);
    }
  }

  * {
    pointer-events: none;
  }
`;

const AmountWrapper = styled(Box)`
  margin-top: 2px;
`;

export const getAmountInfo = (contractAccountBalance: IContractAccountInvoice): IAmountInfo => {
  const {
    accountBalance,
    amountDue,
    amountOverdue,
    instalment,
    paymentType,
  } = contractAccountBalance;
  const isInvoice = () => paymentType === EPaymentType.Invoice;
  const hasOverdueAmount = () => amountOverdue > 0;
  const hasAdditionalCharges = () => amountDue !== amountOverdue;
  const hasNoDueBill = () => amountDue === 0 && accountBalance >= 0;
  const hasNoOverdueDueAmount = () => amountOverdue === 0 && amountDue >= 0;
  const hasCredit = () => accountBalance < 0;

  if (isInvoice()) {
    if (hasOverdueAmount()) {
      if (hasAdditionalCharges()) {
        return {
          description: content.overduePayable,
          amount: amountDue,
        };
      }

      return {
        description: content.overdue,
        amount: amountOverdue,
      };
    }

    if (hasNoDueBill()) {
      return {
        description: content.accountBalance,
        amount: accountBalance,
      };
    }

    if (hasNoOverdueDueAmount() && !hasCredit()) {
      return {
        description: content.payable,
        amount: amountDue,
      };
    }

    if (hasCredit()) {
      return {
        description: content.accountBalance,
        amount: accountBalance * -1,
        isCredit: true,
      };
    }
  }

  return {
    description: instalment.amountOverdue ? content.instalmentOverdue : content.instalment,
    amount: instalment.amountOverdue + instalment.amountDue,
  };
};

export const Amount: React.SFC<IAmountInfo> = ({ amount, description, isCredit }) => (
  <AmountWrapper data-id="account-selector-amount">
    <Text weight={amount === 0 || isCredit ? 'regular' : 'medium'} align="right" data-id="amount">
      <Currency isCredit={isCredit}>{amount / 100}</Currency>
    </Text>
    <Text align="right" data-id="amount-description" variant="body-small" tone="neutralLight">
      {description}
    </Text>
  </AmountWrapper>
);

export const AccountSelector: React.FC<IProps> = ({
  contractAccountBalance,
  isSelected,
  onClick,
  disabled,
}) => {
  const { contractAccountId, divisionCode, type } = contractAccountBalance;
  const divisionCodeType = type || divisionCode;

  const id = 'account-selector-tile';

  const theme = useTheme();
  const { onClickCapture } = useTracking({
    children: id,
    'data-id': id,
    type: 'AccountSelectorTile',
  });

  const amountInfo = getAmountInfo(contractAccountBalance);
  return (
    <AccountSelectorTile
      data-id={id}
      onClick={(e) => {
        e.preventDefault();
        onClick(contractAccountId, amountInfo.isCredit ? 0 : amountInfo.amount);
        onClickCapture();
      }}
      isSelected={isSelected}
      disabled={disabled}
      theme={theme}
    >
      <Columns space="none">
        <Column>
          <Radio
            label={`${divisionIdLabelMap[divisionCodeMap[divisionCodeType]]}${
              contractAccountBalance.status === 'CLOSED' ? content.closed : ''
            }`}
            helperText={formatContractAccount(contractAccountId)}
            name="accountSelector"
            id="accountSelector"
            checked={isSelected}
            onChange={() => {}}
          />
        </Column>
        <Column alignX="right">
          <Amount {...amountInfo} />
        </Column>
      </Columns>
    </AccountSelectorTile>
  );
};
